import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import normalize from 'components/Dashboard/utilities/normalizeString';
import removeHyphenFromString from 'components/Dashboard/utilities/removeHyphenFromString';
import PartyMetaData from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import getAppSite from 'utils/getAppSite';
dayjs.extend(isSameOrAfter);

const createChartDataOptions = () => {
  let options = [
    {
      name: 'Overall',
    },
    {
      name: 'Party',
      bucketField: 'contactParty',
    },
  ];
  if (getAppSite() === 'global') {
    options = [
      ...options,
      {
        name: 'Region',
        bucketField: 'contactRegion',
      },
      {
        name: 'Parliamentary position',
        bucketField: 'contactPositionCategories',
        showRowsFor: ['Backbenchers', 'Government', 'Shadow government', 'MSs', 'MSPs', 'MLAs'],
        filters: [
          {
            field: 'contactPositionCategories',
            value: 'Backbencher',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'Government',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'Shadow Government',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'Shadow Cabinet',
            operator: 'str_eq',
          },
          {
            field: 'contactPositionCategories',
            value: 'Cabinet',
            operator: 'str_eq',
          },
          { field: 'contactPositionCategories', value: 'MS', operator: 'str_eq' },
          { field: 'contactPositionCategories', value: 'MSP', operator: 'str_eq' },
          { field: 'contactPositionCategories', value: 'MLA', operator: 'str_eq' },
        ],
      },
      {
        name: 'Contribution type',
      },
    ];
  }
  if (getAppSite() === 'usa') {
    options = [
      ...options,
      {
        name: 'House',
        bucketField: 'house',
        graphsToShow: ['Mentions', 'Sentiment snapshot', 'Sentiment changes'],
      },
      {
        name: 'State',
        bucketField: 'contactConstituency',
      },
    ];
  }
  return options;
};

const chartDataOptions = createChartDataOptions();

const createDataOptionsMapping = ({ date }) => {
  let currentDate = date ? dayjs(date) : dayjs();
  return [
    {
      name: 'Last 7 days',
      filter: [currentDate.subtract(7, 'day').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-7-days',
    },
    {
      name: 'Last 30 days',
      filter: [currentDate.subtract(29, 'day').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-30-days',
    },
    {
      name: 'Last 3 months',
      filter: [currentDate.subtract(3, 'month').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-3-months',
    },
    {
      name: 'Last 6 months',
      filter: [currentDate.subtract(6, 'month').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-6-months',
    },
    {
      name: 'Last 12 months',
      filter: [currentDate.subtract(1, 'year').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-12-months',
    },
    {
      name: 'Last 2 years',
      filter: [currentDate.subtract(2, 'year').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-two-years',
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Last 3 years',
      filter: [currentDate.subtract(3, 'year').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-three-years',
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Last 5 years',
      filter: [currentDate.subtract(5, 'year').startOf('day').format('YYYY-MM-DD')],
      normalized: 'last-five-years',
      hidden: getAppSite() === 'usa',
    },
    {
      name: 'Custom',
      filterProperty: 'range',
      hidden: getAppSite() === 'usa',
    },
  ];
};

const DateOptionsMapping = createDataOptionsMapping({});

const analyticsStateMapping = {
  currentView: 'resolution',
  activeDateInterval: 'dates',
  sentimentDateInterval: 'sentiment-dates',
  tweetsDateInterval: 'tweets-dates',
  typeOfChart: 'style',
  chartDataOptionSelected: 'group-by',
  selectedCountries: 'nations',
  allowFrontbenchers: 'include-westminster-frontbenchers',
  includeXRepostsTweetsSection: 'include-x-reposts',
};

const getSelectedCategories = () => {
  let hashParts = window.location.hash.replace('#', '').split('~');
  const groupBy = hashParts.find((item) => item.startsWith('group-by:'))?.split(':')?.[1];
  if (groupBy === 'party') {
    return PartyMetaData.showParties;
  } else {
    return null;
  }
};

export const mentionsGraphNations = [
  { name: 'MPs & peers', value: 'UK' },
  { name: 'MSs (Wales)', value: 'Wales' },
  { name: 'MSPs (Scotland)', value: 'Scotland' },
  { name: 'MLAs (NI)', value: 'NorthernIreland' },
];

const createAnalyticsState = (initialState) => {
  let initialObject = {
    currentView: 'Month',
    activeDateInterval: {
      name: 'Last 12 months',
      filter: [dayjs().subtract(1, 'year').startOf('day').format('YYYY-MM-DD')],
    },
    tweetsDateInterval: createTweetsDateIntervalValue(),
    sentimentDateInterval: createActiveDateIntervalSentimentSnapshot(),
    typeOfChart: 'Bar',
    typeOfChartSentimentChanges: 'Line',
    chartDataOptionSelected: 'Overall',
    chartDataOptionSelectedSentimentChanges: 'Party',
    chartDataOptionSelectedTweets: 'Overall',
    typeOfChartInterestByRegion: 'Heat map',
    partySelectedCategories: getSelectedCategories(),
    includeXRepostsTweetsSection: createIncludeRepostsOption({ tweetsDateInterval: createTweetsDateIntervalValue() }),
    selectedCountries: mentionsGraphNations,
    selectedHouseOptions: ['House', 'Senate'],
    selectedCountriesInterestByRegion: [mentionsGraphNations[0]],
    allowFrontbenchers: false,
  };
  if (initialState) {
    return initialObject;
  }

  let createdAnalyticsState = initialObject;
  if (window.location.pathname.includes('/analytics') || window.location.pathname.includes('/embed-analytics')) {
    let hashParts = window.location.hash.replace('#', '').split('~');

    let currentViewHash = hashParts.find((item) => item.includes(`${analyticsStateMapping['currentView']}:`));
    let activeDateIntervalHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['activeDateInterval']}:`)
    );
    let sentimentDateIntervalHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['sentimentDateInterval']}:`)
    );
    let tweetsDateIntervalHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['tweetsDateInterval']}:`)
    );
    let typeOfChartHash = hashParts.find((item) => item.includes(`${analyticsStateMapping['typeOfChart']}:`));
    let chartDataOptionSelectedHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['chartDataOptionSelected']}:`)
    );
    let nationsHash = hashParts.find((item) => item.includes(`${analyticsStateMapping['selectedCountries']}:`));
    let allowFrontbenchersHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['allowFrontbenchers']}:`)
    );
    let includeXRepostsHash = hashParts.find((item) =>
      item.includes(`${analyticsStateMapping['includeXRepostsTweetsSection']}:`)
    );

    //CURRENT VIEW HASH
    if (currentViewHash) {
      createdAnalyticsState.currentView = removeHyphenFromString(currentViewHash.split(':')[1]);
    }

    // Helper to parse date interval hash
    const parseDateIntervalHash = (hash, stateKey) => {
      if (!hash) return;
      const value = hash.split(':')[1];
      let filterValue;
      if (value?.includes('between')) {
        let fromToDates = value.replace('between-', '').split('-and-');
        filterValue = {
          name: 'Custom',
          filter: fromToDates,
        };
        createdAnalyticsState[stateKey] = filterValue;
      } else {
        const option = createDataOptionsMapping({}).find((item) => item.normalized === value);
        if (option) {
          filterValue = {
            name: option.name,
            filter: option.filter,
          };
          createdAnalyticsState[stateKey] = filterValue;
        }
      }
      if (stateKey === 'tweetsDateInterval') {
        const tweetsInterval = filterValue || createdAnalyticsState.tweetsDateInterval;
        if (tweetsInterval) {
          createdAnalyticsState['includeXRepostsTweetsSection'] = createIncludeRepostsOption({
            tweetsDateInterval: tweetsInterval,
          });
        }
      }
    };

    //ACTIVE DATE HASH
    parseDateIntervalHash(activeDateIntervalHash, 'activeDateInterval');
    //SENTIMENT DATE HASH
    parseDateIntervalHash(sentimentDateIntervalHash, 'sentimentDateInterval');
    //TWEETS DATE HASH
    parseDateIntervalHash(tweetsDateIntervalHash, 'tweetsDateInterval');

    //TYPE OF CHART HASH
    if (typeOfChartHash) {
      if (window.location.pathname.includes('/sentiment-changes')) {
        let option = typeOfChartHash.split(':')[1];
        if (option === 'Line' || option === 'Bar') {
          createdAnalyticsState.typeOfChartSentimentChanges = removeHyphenFromString(typeOfChartHash.split(':')[1]);
        }
      } else if (window.location.pathname.includes('/interest-by-region')) {
        createdAnalyticsState.typeOfChartInterestByRegion = removeHyphenFromString(typeOfChartHash.split(':')[1]);
      } else {
        createdAnalyticsState.typeOfChart = removeHyphenFromString(typeOfChartHash.split(':')[1]);
      }
    }

    //CHART DATA OPTION SELECTED HASH
    if (chartDataOptionSelectedHash) {
      chartDataOptions.forEach((item) => {
        const { name } = item;
        if (chartDataOptionSelectedHash.split(':')[1] === normalize(name === 'Overall' ? 'none' : name)) {
          if (window.location.pathname.includes('/analytics/tweets')) {
            createdAnalyticsState.chartDataOptionSelectedTweets = name;
          } else if (window.location.pathname.includes('/analytics/sentiment-changes')) {
            createdAnalyticsState.chartDataOptionSelectedSentimentChanges = name;
          } else {
            createdAnalyticsState.chartDataOptionSelected = name;
          }
        }
      });
    }

    //SELECTED COUNTRIES HASH
    if (nationsHash) {
      let nationsFromHash = nationsHash.split(':')?.[1]?.split('-');
      if (window.location.pathname.includes('/analytics/interest-by-region')) {
        createdAnalyticsState.selectedCountriesInterestByRegion = mentionsGraphNations.filter((item) =>
          nationsFromHash.includes(normalize(item.value))
        );
      } else {
        createdAnalyticsState.selectedCountries = mentionsGraphNations.filter((item) =>
          nationsFromHash.includes(normalize(item.value))
        );
      }
    }

    //ALLOW FRONTBENCHERS
    if (allowFrontbenchersHash) {
      createdAnalyticsState.allowFrontbenchers = allowFrontbenchersHash.split(':')[1] === 'true';
    }

    //INCLUDE X REPOSTS
    if (includeXRepostsHash) {
      createdAnalyticsState.includeXRepostsTweetsSection = includeXRepostsHash.split(':')[1] === 'true';
    }
  }

  if (!createdAnalyticsState.selectedCountriesInterestByRegion.some((item) => item.value === 'UK')) {
    createdAnalyticsState.allowFrontbenchers = false;
  }

  return createdAnalyticsState;
};

const getActiveDateIntervalName = (dateInterval, default12MonthsName = 'last 12 months') => {
  return dateInterval?.name === 'Last 12 months' ? default12MonthsName : dateInterval?.name?.toLowerCase();
};

const sortParties = (items) => {
  const parties = PartyMetaData.showParties;

  return items.sort((a, b) => {
    const indexA = parties.indexOf(a?.text ?? a);
    const indexB = parties.indexOf(b?.text ?? b);

    // If both items are in the parties array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one item is in the parties array, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither item is in the parties array, sort alphabetically
    return a.text ? a.text.localeCompare(b.text) : a.localeCompare(b);
  });
};

const envDays = import.meta.env.VITE_APP_SENTIMENT_SNAPSHOT_NUM_DAYS;
const createActiveDateIntervalSentimentSnapshot = () => {
  switch (envDays) {
    case '365':
      return {
        name: 'Last 12 months',
        filter: [dayjs().subtract(1, 'year').startOf('day').format('YYYY-MM-DD')],
      };
    case '180':
      return {
        name: 'Last 6 months',
        filter: [dayjs().subtract(6, 'month').startOf('day').format('YYYY-MM-DD')],
      };
    case '90':
      return {
        name: 'Last 3 months',
        filter: [dayjs().subtract(3, 'month').startOf('day').format('YYYY-MM-DD')],
      };
    default:
      return {
        name: 'Last 12 months',
        filter: [dayjs().subtract(1, 'year').startOf('day').format('YYYY-MM-DD')],
      };
  }
};

const createTweetsDateIntervalValue = () => {
  const firstRetweetDate = import.meta.env.VITE_APP_FIRST_RETWEET_DATE;
  const maxName =
    firstRetweetDate && dayjs().diff(dayjs(firstRetweetDate), 'month') < 12 ? 'Last 6 months' : 'Last 12 months';
  const firstTweetDate = import.meta.env.VITE_APP_FIRST_TWEET_DATE;
  const optionsMapping = createDataOptionsMapping({});
  let defaultOption = optionsMapping.find((opt) => opt.name === maxName);
  if (!defaultOption) return undefined;

  if (firstTweetDate) {
    const validOptions = optionsMapping.filter((opt) => {
      if (!opt.filter || !opt.filter[0]) return false;
      return dayjs(opt.filter[0]).isSameOrAfter(firstTweetDate);
    });
    if (validOptions.length > 0) {
      let lastValid = validOptions[validOptions.length - 1];
      const order = optionsMapping?.map((item) => item?.name);
      const lastValidIdx = order.indexOf(lastValid.name);
      const maxIdx = order.indexOf(maxName);
      if (lastValidIdx !== -1 && lastValidIdx < maxIdx) {
        return {
          name: lastValid.name,
          filter: lastValid.filter,
        };
      }
    }
  }
  return {
    name: defaultOption.name,
    filter: defaultOption.filter,
  };
};

const createIncludeRepostsOption = ({ tweetsDateInterval }) => {
  const firstRetweetDate = import.meta.env.VITE_APP_FIRST_RETWEET_DATE;
  if (!tweetsDateInterval?.filter?.[0] || !firstRetweetDate) {
    return false;
  }
  return dayjs(tweetsDateInterval.filter[0]).isSameOrAfter(firstRetweetDate);
};

export {
  chartDataOptions,
  createAnalyticsState,
  analyticsStateMapping,
  DateOptionsMapping,
  createDataOptionsMapping,
  sortParties,
  createActiveDateIntervalSentimentSnapshot,
  getActiveDateIntervalName,
  createIncludeRepostsOption,
  createTweetsDateIntervalValue,
};
